import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import '../../styles/index.css';
import FormFieldRender from '../FormRender/FormFieldRender';
import { PersonalDataInitialValues } from '../../constants/initial-values/personal-data-initial-values';
import { useInstitution } from '../hooks/useInstitution';
import { debouncedSetName } from '../common/common-functions/debounsedSetName';
import {
  PersonalDataRequestBody,
  useUpdatePersonalDataMutation,
} from '../../redux/features/pjesemarres/pjesemarres-api';
import {
  NotificationSucces,
  NotificationError,
} from '../common/common-componnets/Notifications';
import { useAuth } from '../../hooks/use-auth';
import { useDispatch } from 'react-redux';
import { authApi } from '../../redux/features/auth/authApi';
import { inputStyling } from '../../constants/common-style-constants';

interface TedhenaPersonaleProps {
  onSuccess?: () => void;
}

const CAUTION_MESSAGE =
  'Të dhënat tuaja aktuale mund të mos jenë të sakta. Ju lutem përditësoni të dhënat. Të dhënat do të përpunohen sipas rregullores së Shkollës së Magjistraturës për "MBROJTJEN, PËRPUNIMIN, RUAJTJEN DHE SIGURINË E TË DHËNAVE PERSONALE" si dhe sipas ligjit nr. 124/2024.';

const HALF_WIDTH_FIELDS = ['numriPersonal', 'ditelindja'];

const TedhenaPersonale = ({ onSuccess }: TedhenaPersonaleProps) => {
  const [form] = useForm();
  const { data: loggedInUser } = useAuth();
  const dispatch = useDispatch();
  const [institutionSearch, setInstitutionSearch] = useState('');
  const { institutionOptions, popupScroll } = useInstitution(institutionSearch);
  const [updatePersonalData, { isLoading }] = useUpdatePersonalDataMutation();

  const fields = PersonalDataInitialValues();

  const institutionSelectOptions = useMemo(() => {
    const base =
      institutionOptions?.map(inst => ({
        value: inst.id,
        label: `${inst.emri} - ${inst.qyteti ?? ''}`,
      })) ?? [];
    const current = loggedInUser?.institucioni;
    if (current && !base.some(o => o.value === current.id)) {
      return [
        {
          value: current.id,
          label: `${current?.emri} - ${current?.qyteti ?? ''}`,
        },
        ...base,
      ];
    }
    return base;
  }, [institutionOptions, loggedInUser?.institucioni]);

  useEffect(() => {
    if (loggedInUser?.personi) {
      const p = loggedInUser.personi;
      form.setFieldsValue({
        ...loggedInUser,
        ...p,
        ditelindja: p.ditelindja ? dayjs(p.ditelindja) : undefined,
        institucioni: loggedInUser.institucioni?.id,
        fushatVeprimtarise: loggedInUser.fushatVeprimtarise,
      });
    }
  }, [loggedInUser]);

  const handleSubmit = (values: Record<string, unknown>) => {
    const body = {
      ...values,
      emri: undefined,
      mbiemri: undefined,
      ditelindja: dayjs(values.ditelindja as dayjs.ConfigType).format(
        'YYYY-MM-DD',
      ),
      institucioni: { id: values.institucioni as number },
    };

    updatePersonalData(body as PersonalDataRequestBody)
      .unwrap()
      .then(() => {
        NotificationSucces('Të dhënat personale u ruajtën me sukses');
        dispatch(authApi.util.resetApiState());
        onSuccess?.();
      })
      .catch(error => {
        NotificationError(error?.data?.message ?? 'Ndodhi një gabim');
      });
  };

  return (
    <Form
      form={form}
      requiredMark={false}
      className="m-3"
      name="tedhenaPersonale"
      onFinish={handleSubmit}
      layout="vertical"
    >
      <Alert
        className="m-3"
        type="warning"
        showIcon
        message={CAUTION_MESSAGE}
      />

      <div className="grid grid-cols-2">
        {fields.map((data, i) => (
          <React.Fragment key={i}>
            <div
              className={`${
                data.type === 'checkbox'
                  ? 'checkbox-placeholder'
                  : 'input-with-placeholder'
              } m-3 ${
                HALF_WIDTH_FIELDS.includes(data.name as string)
                  ? 'col-span-2 sm:col-span-1'
                  : 'col-span-2'
              }`}
            >
              <span className="form-item-label text-sm font-medium text-gray-400">
                {data?.label}
              </span>
              <FormFieldRender data={data} />
            </div>
          </React.Fragment>
        ))}

        <div className="input-with-placeholder col-span-2 m-3">
          <span className="form-item-label text-sm font-medium text-gray-400">
            Institucioni
          </span>
          <Form.Item name="institucioni">
            <Select
              className={inputStyling}
              placeholder="Zgjidhni institucionin"
              onPopupScroll={popupScroll}
              showSearch
              filterOption={false}
              onSearch={value => debouncedSetName(value, setInstitutionSearch)}
              onDropdownVisibleChange={open => {
                if (!open) setInstitutionSearch('');
              }}
              options={institutionSelectOptions}
            />
          </Form.Item>
        </div>
      </div>

      <div className="m-3 flex justify-end">
        <Button
          loading={isLoading}
          name="Submit"
          className="submitButton block h-11 w-full rounded text-base font-semibold text-white"
          htmlType="submit"
          style={{ backgroundColor: '#FF731D' }}
        >
          Ruaj
        </Button>
      </div>
    </Form>
  );
};

export default TedhenaPersonale;
